import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { addStyles } from '../Layout/utils/addStyles';

interface NewWindowProps {
  children: ReactNode;
  url?: string;
  name?: string;
  title?: string;
  features?: Record<string, string | number | boolean | undefined>;
  onUnload?: () => void;
  onBlock?: () => void;
  onOpen?: (popoutWindow: Window) => void;
  copyStyles?: boolean;
}

// Default dimensions for the popout window.
const defaultFeatures = { width: '600px', height: '400px' };

/** Create a new browser window for rendering children content via portal. */
function NewWindow({
  children,
  url = '',
  name = '',
  title = '',
  features = defaultFeatures,
  onUnload = () => {},
  onBlock = () => {},
  onOpen = () => {},
  copyStyles = true,
}: NewWindowProps) {
  const [mounted, setMounted] = useState(false);
  const containerRef = useRef<HTMLElement | null>(null);
  const windowRef = useRef<Window | null>(null);
  const windowCheckerInterval = useRef<ReturnType<typeof setInterval>>();
  const released = useRef(false);

  const release = useCallback(() => {
    // Cleanup logic to clear interval and invoke the unload callback once.
    if (released.current) {
      return;
    }
    released.current = true;

    clearInterval(windowCheckerInterval.current);
    onUnload();
  }, [onUnload]);

  // Effect to open the new window, setup the container div, copy styles, and manage lifecycle events.
  useEffect(() => {
    if (windowRef.current || containerRef.current) {
      return;
    }

    const newWindow = window.open(url, name, toWindowFeatures(features));
    windowRef.current = newWindow;

    // Poll to check if the window has been closed manually and trigger cleanup.
    const checkWindowClosed = () => {
      if (!windowRef.current || windowRef.current.closed) {
        release();
      }
    };
    windowCheckerInterval.current = setInterval(checkWindowClosed, 50);

    if (newWindow) {
      newWindow.addEventListener('load', () => {
        if (!windowRef.current) {
          onBlock();
          return;
        }

        windowRef.current.document.title = title;

        let container = windowRef.current.document.getElementById('new-window-container');
        if (!container) {
          container = windowRef.current.document.createElement('div');
          container.id = 'new-window-container';
          container.setAttribute('data-testid', 'new-window-container');
          container.style.position = 'absolute';
          container.style.inset = '0';
          windowRef.current.document.body.appendChild(container);
        } else {
          const staticContainer = windowRef.current.document.getElementById('new-window-container-static');
          if (staticContainer) {
            windowRef.current.document.body.removeChild(staticContainer);
          }
        }

        containerRef.current = container;

        if (copyStyles) {
          setTimeout(() => {
            if (windowRef.current) {
              addStyles(windowRef.current.document, document.styleSheets, undefined);
            }
          }, 0);
        }

        // Mark the component as mounted and call the onOpen handler with the new window reference.
        setMounted(true);
        onOpen(windowRef.current);

        newWindow.addEventListener('beforeunload', () => release());
      });
    } else {
      onBlock?.();
    }

    return () => {
      if (mounted && windowRef.current) {
        windowRef.current.close();
      }
    };
  }, [children, copyStyles, features, mounted, name, onBlock, onOpen, release, title, url]);

  if (!mounted || !containerRef.current) {
    return null;
  }

  return ReactDOM.createPortal(children, containerRef.current, title);
}

/** Converts the features object to a string suitable for window.open(). */
function toWindowFeatures(features: Record<string, string | number | boolean | undefined>) {
  return Object.entries(features)
    .reduce((featuresString: string[], [name, value]) => {
      if (typeof value === 'boolean') {
        featuresString.push(`${name}=${value ? 'yes' : 'no'}`);
      } else if (value !== undefined) {
        featuresString.push(`${name}=${value}`);
      }
      return featuresString;
    }, [])
    .join(',');
}

export default NewWindow;
